import React from 'react';

// Performance logging utility for tracking component and operation timing

interface PerformanceMetric {
  name: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  metadata?: Record<string, any>;
}

class PerformanceLogger {
  private metrics: Map<string, PerformanceMetric> = new Map();
  private pageStartTime: number = performance.now();

  // Start timing a specific operation
  startTimer(name: string, metadata?: Record<string, any>) {
    const startTime = performance.now();
    this.metrics.set(name, {
      name,
      startTime,
      metadata,
    });
    console.log(`🚀 [Performance] Started: ${name}`, metadata || '');
  }

  // End timing for a specific operation
  endTimer(name: string, additionalMetadata?: Record<string, any>) {
    const metric = this.metrics.get(name);
    if (!metric) {
      console.warn(`⚠️ [Performance] Timer not found: ${name}`);
      return;
    }

    const endTime = performance.now();
    const duration = endTime - metric.startTime;

    metric.endTime = endTime;
    metric.duration = duration;

    if (additionalMetadata) {
      metric.metadata = { ...metric.metadata, ...additionalMetadata };
    }

    console.log(
      `✅ [Performance] Completed: ${name} - ${duration.toFixed(2)}ms`,
      metric.metadata || '',
    );
  }

  // Log a single timing measurement
  logTiming(name: string, duration: number, metadata?: Record<string, any>) {
    console.log(
      `⏱️ [Performance] ${name}: ${duration.toFixed(2)}ms`,
      metadata || '',
    );
  }

  // Get total page load time
  getPageLoadTime(): number {
    return performance.now() - this.pageStartTime;
  }

  // Log all metrics and generate a summary
  logSummary() {
    const totalTime = this.getPageLoadTime();
    console.log(
      `📊 [Performance] Page Load Summary - Total: ${totalTime.toFixed(2)}ms`,
    );

    const sortedMetrics = Array.from(this.metrics.values())
      .filter((m) => m.duration !== undefined)
      .sort((a, b) => (b.duration || 0) - (a.duration || 0));

    console.table(
      sortedMetrics.map((m) => ({
        Name: m.name,
        Duration: `${m.duration?.toFixed(2)}ms`,
        Percentage: `${(((m.duration || 0) / totalTime) * 100).toFixed(1)}%`,
        Metadata: m.metadata ? JSON.stringify(m.metadata) : '',
      })),
    );
  }

  // Clear all metrics
  clear() {
    this.metrics.clear();
    this.pageStartTime = performance.now();
  }
}

// Create a singleton instance
export const performanceLogger = new PerformanceLogger();

// React hook for component timing
export const useComponentTimer = (
  componentName: string,
  dependencies: any[] = [],
) => {
  React.useEffect(() => {
    performanceLogger.startTimer(`${componentName} - Mount`);

    return () => {
      performanceLogger.endTimer(`${componentName} - Mount`);
    };
  }, dependencies);
};

// Higher-order component for timing
export const withPerformanceTimer = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  componentName: string,
) => {
  return React.forwardRef<any, P>((props, ref) => {
    useComponentTimer(componentName);
    return React.createElement(WrappedComponent, { ...props, ref } as any);
  });
};

// Utility for timing async operations
export const timeAsyncOperation = async <T>(
  name: string,
  operation: () => Promise<T>,
  metadata?: Record<string, any>,
): Promise<T> => {
  performanceLogger.startTimer(name, metadata);
  try {
    const result = await operation();
    performanceLogger.endTimer(name, { success: true });
    return result;
  } catch (error) {
    performanceLogger.endTimer(name, {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

// Utility for timing synchronous operations
export const timeSyncOperation = <T>(
  name: string,
  operation: () => T,
  metadata?: Record<string, any>,
): T => {
  performanceLogger.startTimer(name, metadata);
  try {
    const result = operation();
    performanceLogger.endTimer(name, { success: true });
    return result;
  } catch (error) {
    performanceLogger.endTimer(name, {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};
